import {
  CrmFileCard,
  CrmVenueImpact,
  CrmIssues,
  CrmResult,
  CrmSummary,
  CrmRowFilters,
  matchesCrmFilter,
  type CrmRowFilter,
} from './CrmWorkflowParts';
import { crmErrorCode, crmErrorText, CrmErrorDetails } from './CrmError';
import { useCrmAdapter } from './CrmWorkflowContext';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  MemberOpeningDecision,
  MemberOpeningPreview,
  MemberOpeningResult,
  MemberOpeningRouting,
  MemberOpeningRoutingVenues,
  MemberOpeningStatus,
} from '@pokerroom/types';
import { useAuthStore } from '../../store/auth.store';
import { api } from '../../lib/api';
import { ALERT, CHECK_LABEL, CHECKBOX, FIELD, OUTLINE, PRIMARY } from './importStyles';

const LABEL = 'block text-sm text-base-content mb-1';
const NUM = 'text-right tabular-nums';

export default function TimedOpeningPanel({ entryVenueId }: { entryVenueId?: string }) {
  const identity = useAuthStore((s) => `${s.user?.id ?? ''}:${s.sessionRevision}`);
  return <TimedOpeningForm key={`${identity}:${entryVenueId ?? ''}`} entryVenueId={entryVenueId} />;
}

function TimedOpeningForm({ entryVenueId }: { entryVenueId?: string }) {
  const { t } = useTranslation('member');
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<MemberOpeningPreview | null>(null);
  const [decisions, setDecisions] = useState<Record<string, MemberOpeningDecision>>({});
  const [excludedSourceKeys, setExcludedSourceKeys] = useState<string[]>([]);
  const [fileFormat, setFileFormat] = useState<'DETAIL' | 'LEGACY'>('DETAIL');
  const [fresh, setFresh] = useState(false);
  const [result, setResult] = useState<MemberOpeningResult | null>(null);
  const [error, setError] = useState('');
  const actorId = useAuthStore((s) => s.user?.id ?? '');
  const session = useAuthStore((s) => s.sessionRevision);
  const venuesQuery = useQuery({
    queryKey: ['opening-routing-venues', actorId, session, entryVenueId],
    queryFn: () =>
      api.get<MemberOpeningRoutingVenues>('/members/import/timed-opening/routing-venues'),
    enabled: Boolean(actorId),
    retry: false,
    staleTime: 30000,
  });
  const context =
    venuesQuery.data &&
    (!entryVenueId || venuesQuery.data.entryVenueId === entryVenueId) &&
    typeof venuesQuery.data.environment === 'string' &&
    venuesQuery.data.environment.trim()
      ? venuesQuery.data
      : undefined;
  const [rules, setRules] = useState<MemberOpeningRouting['rules']>([]);
  const [assignments, setAssignments] = useState<Record<number, string>>({});
  const [checking, setChecking] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [recoveryState, setRecoveryState] = useState('');
  const [visible, setVisible] = useState(50);
  const [rowFilter, setRowFilter] = useState<CrmRowFilter>({ venue: '', blockedOnly: false });
  const sentAttempt = useRef<{ hash: string; added: boolean } | null>(null);
  const recoveryKey = context
    ? `opening-attempts:${actorId}:${context.entryVenueId}:${context.environment}`
    : '';
  const readAttempts = (): string[] => {
    if (!recoveryKey) return [];
    try {
      const value: unknown = JSON.parse(sessionStorage.getItem(recoveryKey) ?? '[]');
      if (
        !Array.isArray(value) ||
        value.length > 10 ||
        value.some((v) => typeof v !== 'string' || !/^[a-f0-9]{64}$/.test(v))
      )
        throw new Error('Opening recovery corrupt');
      return value;
    } catch {
      throw new Error('Opening recovery corrupt');
    }
  };

  const routingChanged = (clearAssignments = false) => {
    if (clearAssignments) setAssignments({});
    setDecisions({});
    setExcludedSourceKeys([]);
    setFresh(false);
    setError('');
  };
  const recover = async () => {
    const token = workflow.begin('lookup');
    if (!token) return;
    setChecking(true);
    setUncertain(true);
    try {
      const attempts = readAttempts();
      const remaining = [...attempts];
      let state = attempts.length ? 'NOT_CONFIRMED' : '';
      for (const planHash of attempts) {
        const value = await api.get<MemberOpeningStatus>('/members/import/timed-opening/status', {
          params: { planHash },
        });
        if (value.state === 'CONFIRMED' && value.result && value.result.planHash === planHash) {
          setResult(value.result);
          remaining.splice(remaining.indexOf(planHash), 1);
          sessionStorage.setItem(recoveryKey, JSON.stringify(remaining));
          state = 'CONFIRMED';
        } else if (value.state === 'PENDING' || value.state === 'CONFLICT') state = value.state;
      }
      setUncertain(remaining.length > 0);
      setRecoveryState(remaining.length && state === 'CONFIRMED' ? 'NOT_CONFIRMED' : state);
    } catch {
      setRecoveryState('ERROR');
      setUncertain(true);
    } finally {
      workflow.end(token);
      setChecking(false);
    }
  };
  useEffect(() => {
    if (recoveryKey) void recover();
  }, [recoveryKey]);
  const routing = (): MemberOpeningRouting => {
    const grouped = new Map<string, number[]>();
    for (const [row, venueId] of Object.entries(assignments))
      if (venueId) grouped.set(venueId, [...(grouped.get(venueId) ?? []), Number(row)]);
    return {
      version: 'VENUE_DETAIL_OCT_2026_V1',
      rules,
      assignments: [...grouped].map(([venueId, rowNumbers]) => ({
        venueId,
        rowNumbers,
        fileSha256: preview?.files[0]?.sha256 ?? '',
      })),
    };
  };

  const form = () => {
    const data = new FormData();
    files.forEach((file) => data.append('files', file));
    data.append(
      'payload',
      JSON.stringify({
        decisions: Object.values(decisions),
        excludedSourceKeys,
        ...(fileFormat === 'DETAIL' ? { routing: routing() } : {}),
        ...(preview ? { planHash: preview.planHash } : {}),
      }),
    );
    return data;
  };
  const [technicalError, setTechnicalError] = useState('');
  const fail = (e: unknown) => {
    const code = crmErrorCode(e);
    setTechnicalError(code);
    setError(crmErrorText(t, code));
    setFresh(false);
  };
  const dryRun = useMutation({
    mutationFn: () =>
      api.post<MemberOpeningPreview>('/members/import/timed-opening/preview', form(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    onSuccess: (value) => {
      setPreview(value);
      setFresh(true);
      setError('');
      // Existing operation lookup remains an explicit action after this mutation settles.
    },
    onError: (error) => {
      fail(error);
      // The status button is available after the mutation releases its synchronous lock.
    },
  });
  const confirm = useMutation({
    mutationFn: () => {
      sentAttempt.current = null;
      if (!preview || !context || !recoveryKey) throw new Error('Missing opening context');
      const attempts = readAttempts();
      const added = !attempts.includes(preview.planHash);
      if (added) attempts.push(preview.planHash);
      if (attempts.length > 10) throw new Error('Opening recovery limit');
      sessionStorage.setItem(recoveryKey, JSON.stringify(attempts));
      sentAttempt.current = { hash: preview.planHash, added };
      return api.post<MemberOpeningResult>('/members/import/timed-opening/confirm', form(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (value) => {
      setResult(value);
      try {
        const remaining = readAttempts().filter((hash) => hash !== value.planHash);
        sessionStorage.setItem(recoveryKey, JSON.stringify(remaining));
        setUncertain(remaining.length > 0);
        setError('');
      } catch (error) {
        setUncertain(true);
        setRecoveryState('ERROR');
        fail(error);
      }
    },
    onError: (error) => {
      const code = (error as { response?: { data?: { errorCode?: string; code?: string } } })
        .response?.data;
      // Remove only a newly added attempt with an explicit transaction rollback.
      // A prior uncertain send of this hash must remain recoverable.
      const definiteRollback =
        sentAttempt.current?.added &&
        ((error as { response?: { status?: number } }).response?.status === 400 ||
          ['OPENING_PLAN_STALE', 'OPENING_ROUTING_HOLD', 'OPENING_RECONCILIATION_HOLD'].includes(
            code?.errorCode ?? code?.code ?? '',
          ));
      try {
        if (definiteRollback) {
          sessionStorage.setItem(
            recoveryKey,
            JSON.stringify(readAttempts().filter((h) => h !== sentAttempt.current!.hash)),
          );
        }
        setUncertain(definiteRollback ? readAttempts().length > 0 : true);
        setRecoveryState(definiteRollback ? 'NOT_CONFIRMED' : 'ERROR');
      } catch {
        setUncertain(true);
        setRecoveryState('ERROR');
      }
      fail(error);
      // The status button is available after the mutation releases its synchronous lock.
    },
  });
  const workflow = useCrmAdapter(
    {
      purpose: 'OPENING',
      phase: uncertain
        ? 'UNKNOWN'
        : result
          ? 'RESULT'
          : preview && fresh
            ? 'CONFIRM'
            : preview
              ? 'PREVIEW'
              : 'FILES',
      dirty: !result && (files.length > 0 || !!preview),
      busy: dryRun.isPending || confirm.isPending || checking,
      unresolved: uncertain,
      canPreview: files.length === (fileFormat === 'DETAIL' ? 1 : 2) && !uncertain && !!context,
      canConfirm: !!preview?.ready && fresh && !uncertain && !!context,
    },
    {
      preview: () => requestPreview(),
      confirm: () => guardedConfirm(),
      back: () => {
        setPreview(null);
        setFresh(false);
      },
    },
  );
  const guardedConfirm = () => {
    if (!preview?.ready || !fresh || uncertain || !context) return;
    const token = workflow.begin();
    if (!token) return;
    confirm.mutate(undefined, { onSettled: () => workflow.end(token) });
  };
  const requestPreview = () => {
    if (!context || venuesQuery.isError || uncertain) return;
    if (fileFormat === 'DETAIL' && rules.some((r) => !r.keyword.trim() || !r.venueId)) {
      setError(t('opening.routingInvalid'));
      return;
    }
    const invalid = Object.values(decisions).some(
      (d) =>
        !d.explanation.trim() ||
        (d.evidenceMode !== 'OPERATOR_STATEMENT' &&
          (!/^[A-Za-z0-9][A-Za-z0-9:/._-]*$/.test(d.evidenceReference) ||
            !/^[a-f0-9]{64}$/.test(d.evidenceDigest))) ||
        !d.attested,
    );
    if (invalid) {
      setError(t('opening.evidenceIncomplete'));
      setFresh(false);
      return;
    }
    const token = workflow.begin();
    if (!token) return;
    dryRun.mutate(undefined, { onSettled: () => workflow.end(token) });
  };
  const pending = dryRun.isPending || confirm.isPending || checking;
  const update = (key: string, decision: MemberOpeningDecision) => {
    setDecisions((previous) => ({ ...previous, [key]: decision }));
    setFresh(false);
  };
  return (
    <section className="min-w-0 w-full space-y-4" aria-label={t('opening.title')}>
      <h2 className="text-xl font-semibold text-base-content">{t('opening.title')}</h2>
      <p className="text-sm text-base-content">{t('opening.scope')}</p>
      {venuesQuery.isError && (
        <p role="alert" className={ALERT}>
          {t('opening.venuesFailed')}{' '}
          <button type="button" className={OUTLINE} onClick={() => void venuesQuery.refetch()}>
            {t('opening.retryVenues')}
          </button>
        </p>
      )}
      {recoveryState && (
        <div role="status" className="border border-warning p-3 text-base-content">
          <p>{t(`opening.recovery.${recoveryState}`)}</p>
          {(!result || uncertain) && (
            <button
              type="button"
              className={OUTLINE}
              disabled={pending}
              onClick={() => void recover()}
            >
              {t('opening.checkStatus')}
            </button>
          )}
        </div>
      )}
      <fieldset disabled={pending || Boolean(result)} className="min-w-0 w-full space-y-4">
        <label className="block space-y-1">
          <span>{t('crmWorkspace.openingFormat')}</span>
          <select
            className={FIELD}
            value={fileFormat}
            onChange={(event) => {
              setFileFormat(event.target.value as 'DETAIL' | 'LEGACY');
              setFiles([]);
              setPreview(null);
              setResult(null);
              setDecisions({});
              setAssignments({});
              setExcludedSourceKeys([]);
              setFresh(false);
            }}
          >
            <option value="DETAIL">{t('crmWorkspace.detailFormat')}</option>
            <option value="LEGACY">{t('crmWorkspace.legacyFormat')}</option>
          </select>
        </label>
        {fileFormat === 'DETAIL' && (
          <section className="space-y-3" aria-label={t('opening.routingTitle')}>
            <h3 className="font-semibold">{t('opening.routingTitle')}</h3>
            <p className="text-sm">{t('opening.routingHelp')}</p>
            {rules.map((rule, index) => (
              <div
                key={index}
                className="flex flex-wrap items-end gap-2 border border-base-300 p-3"
              >
                <label className="min-w-0 flex-1">
                  <span className={LABEL}>
                    {t('opening.keyword')} {index + 1}
                  </span>
                  <input
                    className={FIELD}
                    maxLength={100}
                    value={rule.keyword}
                    onChange={(e) => {
                      setRules(
                        rules.map((r, i) => (i === index ? { ...r, keyword: e.target.value } : r)),
                      );
                      routingChanged(true);
                    }}
                  />
                </label>
                <label className="min-w-0 flex-1">
                  <span className={LABEL}>
                    {t('opening.destination')} {index + 1}
                  </span>
                  <select
                    className={FIELD}
                    value={rule.venueId}
                    onChange={(e) => {
                      setRules(
                        rules.map((r, i) => (i === index ? { ...r, venueId: e.target.value } : r)),
                      );
                      routingChanged(true);
                    }}
                  >
                    <option value="">{t('opening.chooseVenue')}</option>
                    {context?.venues.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className={OUTLINE}
                  aria-label={`${t('opening.removeRule')} ${index + 1}`}
                  onClick={() => {
                    setRules(rules.filter((_, i) => i !== index));
                    routingChanged(true);
                  }}
                >
                  {t('opening.removeRule')}
                </button>
              </div>
            ))}
            <button
              type="button"
              className={OUTLINE}
              disabled={!context || rules.length >= 100}
              onClick={() => {
                setRules([...rules, { keyword: '', venueId: '' }]);
                routingChanged(true);
              }}
            >
              {t('opening.addRule')}
            </button>
          </section>
        )}
        <CrmFileCard>
          <label className="block">
            <span className={LABEL}>{t('opening.files')}</span>
            <input
              className="block w-full text-sm text-base-content file:mr-3 file:min-h-11 file:cursor-pointer file:border file:border-base-300 file:bg-base-100 file:px-4 file:text-sm file:font-medium file:text-base-content hover:file:bg-base-300"
              key={fileFormat}
              multiple={fileFormat === 'LEGACY'}
              type="file"
              accept=".xlsx"
              onChange={(event) => {
                setFiles(Array.from(event.target.files ?? []));
                setAssignments({});
                setVisible(50);
                setPreview(null);
                setDecisions({});
                setExcludedSourceKeys([]);
                setFresh(false);
                setError('');
              }}
            />
          </label>
        </CrmFileCard>
        {!workflow.embedded && (
          <button
            type="button"
            className={preview ? OUTLINE : PRIMARY}
            disabled={files.length !== (fileFormat === 'DETAIL' ? 1 : 2) || !context || uncertain}
            onClick={requestPreview}
          >
            {t('opening.preview')}
          </button>
        )}
        {preview && !result && (
          <CrmSummary
            rows={preview.rows.length}
            issues={
              preview.rows.filter((r) => r.holds.length > 0).length +
              (preview.routingIssues?.length ?? 0)
            }
            facts={[
              {
                label: t('crmWorkspace.membersCount'),
                value: new Set(preview.rows.flatMap((r) => (r.memberId ? [r.memberId] : []))).size,
              },
              { label: t('crmWorkspace.venuesCount'), value: preview.venues?.length ?? 0 },
              {
                label: t('crmWorkspace.sourcePeriod'),
                value: preview.periods.map((p) => p.period).join(', '),
              },
            ]}
            ready={preview.ready && fresh}
          />
        )}
        {preview && (
          <>
            <CrmIssues codes={preview.rows.flatMap((row) => row.holds)} />
            {preview.cutoverConflict && (
              <p role="alert" className={ALERT}>
                {t('opening.cutoverExists')}
              </p>
            )}
            {(preview.routingSummary?.length ?? 0) > 0 && (
              <details open>
                <summary className="min-h-11 cursor-pointer font-semibold">
                  {t('opening.routingSummary')}
                </summary>
                <div className="space-y-2">
                  {preview.routingSummary?.slice(0, visible).map((item, index) => (
                    <p className="break-words border-b border-base-300 py-2 text-sm" key={index}>
                      {item.remark || t('opening.blankRemark')} →{' '}
                      {context?.venues.find((v) => v.id === item.venueId)?.name ??
                        t('opening.unresolved')}{' '}
                      · {t('opening.rows')}: {item.rows} · {item.source} ·{' '}
                      {item.assigned
                        ? t('opening.manuallyAssigned')
                        : `${t('opening.autoMatched')}: ${item.ruleIds.map((id) => [...rules].sort((a, b) => (a.keyword.normalize('NFKC').trim().toLowerCase() < b.keyword.normalize('NFKC').trim().toLowerCase() ? -1 : 1))[id]?.keyword ?? '').join(', ')}`}
                    </p>
                  ))}
                </div>
              </details>
            )}
            {(preview.routingIssues?.length ?? 0) > 0 && (
              <section className="space-y-3" aria-label={t('opening.unresolvedRouting')}>
                <h3 className="font-semibold">{t('opening.unresolvedRouting')}</h3>
                {preview.routingIssues?.slice(0, visible).map((issue) => (
                  <label key={issue.rowNumber} className="block border border-warning p-3">
                    <span className={LABEL}>
                      {t('opening.sourceRows')}: {issue.rowNumber} ·{' '}
                      {issue.remark || t('opening.blankRemark')}
                    </span>
                    <select
                      className={FIELD}
                      value={assignments[issue.rowNumber] ?? ''}
                      onChange={(e) => {
                        setAssignments((a) => ({ ...a, [issue.rowNumber]: e.target.value }));
                        routingChanged();
                      }}
                    >
                      <option value="">{t('opening.chooseVenue')}</option>
                      {context?.venues.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </section>
            )}
            {preview.venues && (
              <CrmVenueImpact
                columns={[
                  'destination',
                  'sourceRowCount',
                  'rows',
                  'source',
                  'overlap',
                  'opening',
                  'excludedRows',
                ].map((k) => t(`opening.${k}`))}
                rows={preview.venues.map((v) => ({
                  key: v.venueId + v.period,
                  cells: [
                    v.name,
                    v.sourceRows,
                    v.rows,
                    v.source,
                    v.overlap,
                    v.opening,
                    `${v.excludedRows} / ${v.excludedAmount}`,
                  ],
                }))}
              />
            )}
            {files.length === 1 &&
              preview.rows.some(
                (row) => row.holds.includes('PHONE_UNMATCHED') || row.state === 'SKIPPED_UNMATCHED',
              ) && (
                <label className={`${CHECK_LABEL} items-start border border-warning p-3`}>
                  <input
                    type="checkbox"
                    className={CHECKBOX}
                    checked={excludedSourceKeys.length > 0}
                    onChange={(event) => {
                      setExcludedSourceKeys(
                        event.target.checked
                          ? preview.rows
                              .filter(
                                (row) =>
                                  row.holds.includes('PHONE_UNMATCHED') ||
                                  row.state === 'SKIPPED_UNMATCHED',
                              )
                              .map((row) => row.sourceKey)
                          : [],
                      );
                      setFresh(false);
                    }}
                  />
                  {t('opening.excludeUnmatched')}
                </label>
              )}
            {preview.periods.map(
              (p) =>
                (p.excludedRows ?? 0) > 0 && (
                  <p role="status" key={p.period} className="border border-warning p-3">
                    {t('opening.excludedSummary', {
                      count: p.excludedRows,
                      amount: p.excludedAmount,
                    })}
                  </p>
                ),
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm text-base-content [&_th]:p-2 [&_td]:p-2 [&_td]:whitespace-nowrap">
                <caption className="caption-top p-2 text-left text-sm text-base-content">
                  {t('opening.provenance')}
                </caption>
                <thead>
                  <tr className="border-b border-base-300">
                    {['period', 'rows', 'source', 'overlap', 'opening', 'expiry'].map((key) => (
                      <th
                        key={key}
                        scope="col"
                        className={`font-semibold ${['rows', 'source', 'overlap', 'opening'].includes(key) ? 'text-right' : ''}`}
                      >
                        {t(`opening.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.periods.map((p) => (
                    <tr key={p.period} className="border-b border-base-300">
                      <td>{p.period}</td>
                      <td className={NUM}>{p.rows}</td>
                      <td className={NUM}>{p.source}</td>
                      <td className={NUM}>{p.overlap}</td>
                      <td className={NUM}>{p.opening}</td>
                      <td>
                        {p.lastValidDay}
                        <br />
                        {p.expiresAt}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CrmRowFilters
              value={rowFilter}
              onChange={setRowFilter}
              venues={context?.venues ?? []}
              total={preview.rows.length}
              matched={preview.rows.filter((row) => matchesCrmFilter(row, rowFilter)).length}
              limit={visible}
              onMore={() => setVisible((n) => n + 50)}
            />
            {preview.rows
              .filter((row) => matchesCrmFilter(row, rowFilter))
              .slice(0, visible)
              .map((row) => {
                const key = `${row.sourceKey}|${row.period}`;
                const decision = decisions[key] ?? {
                  sourceKey: row.sourceKey,
                  period: row.period,
                  state: 'NO_OVERLAP' as const,
                  evidenceMode: 'EXTERNAL' as const,
                  explanation: '',
                  evidenceReference: '',
                  evidenceDigest: '',
                  sourceCutoff: preview.files.find((f) => f.period === row.period)!.exportedAt,
                  attested: false,
                  checkedLedgerIds: row.ledger.map((l) => l.id),
                  allocations: [],
                };
                return (
                  <details
                    key={key}
                    className="border border-base-300 px-3 text-sm text-base-content"
                    open={row.holds.length > 0 || row.state === 'SKIPPED_UNMATCHED'}
                  >
                    <summary className="flex min-h-11 cursor-pointer flex-wrap items-center break-all py-2 tabular-nums">
                      {row.period} · {context?.venues.find((v) => v.id === row.venueId)?.name ?? ''}{' '}
                      · {row.sourceKey} · {t(`opening.states.${row.state}`)} · C=
                      {row.sourceAmount} / B={row.beforeBalance} / O={row.opening} / B+O=
                      {row.resultingBalance}
                    </summary>
                    {row.sourceRows && (
                      <p className="mb-2 break-words">
                        {t('opening.sourceRows')}: {row.sourceRows.join(', ')}
                      </p>
                    )}
                    {row.holds.map((hold) => (
                      <p className={`${ALERT} mb-2`} key={hold}>
                        {crmErrorText(t, hold)}
                      </p>
                    ))}
                    {row.ledger.length > 0 && (
                      <div className="space-y-3 pb-3">
                        <p>{t('opening.evidenceHelp')}</p>
                        <label className="block">
                          <span className={LABEL}>{t('opening.decision')}</span>
                          <select
                            className={`${FIELD} max-w-sm`}
                            value={decisions[key]?.state ?? ''}
                            onChange={(e) => {
                              if (!e.target.value) {
                                setDecisions((previous) => {
                                  const next = { ...previous };
                                  delete next[key];
                                  return next;
                                });
                                setFresh(false);
                              } else
                                update(key, {
                                  ...decision,
                                  state: e.target.value as MemberOpeningDecision['state'],
                                  evidenceMode:
                                    e.target.value === 'NO_OVERLAP' &&
                                    row.ledger.every(
                                      (entry) =>
                                        entry.source === 'TOURNAMENT' && Number(entry.delta) > 0,
                                    )
                                      ? 'OPERATOR_STATEMENT'
                                      : 'EXTERNAL',
                                  evidenceReference: '',
                                  evidenceDigest: '',
                                  attested: false,
                                  allocations: [],
                                });
                            }}
                          >
                            <option value="">{t('opening.states.HOLD')}</option>
                            <option value="NO_OVERLAP">{t('opening.states.NO_OVERLAP')}</option>
                            <option value="PROVEN_OVERLAP">
                              {t('opening.states.PROVEN_OVERLAP')}
                            </option>
                          </select>
                        </label>
                        {decision.evidenceMode === 'OPERATOR_STATEMENT' && (
                          <p role="status">{t('opening.statementHelp')}</p>
                        )}
                        {(['explanation', 'evidenceReference', 'evidenceDigest'] as const)
                          .filter(
                            (field) =>
                              field === 'explanation' ||
                              decision.evidenceMode !== 'OPERATOR_STATEMENT',
                          )
                          .map((field) => (
                            <label className="block" key={field}>
                              <span className={LABEL}>{t(`opening.${field}`)}</span>
                              <input
                                className={FIELD}
                                maxLength={
                                  field === 'explanation'
                                    ? 1000
                                    : field === 'evidenceDigest'
                                      ? 64
                                      : 255
                                }
                                value={decision[field] ?? ''}
                                onChange={(e) =>
                                  update(key, { ...decision, [field]: e.target.value })
                                }
                              />
                            </label>
                          ))}
                        <p className="tabular-nums">
                          {t('opening.cutoff')}: {decision.sourceCutoff}
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-left [&_th]:p-2 [&_td]:p-2">
                            <caption className="caption-top p-2 text-left font-semibold">
                              {t('opening.ledger')} · {row.sourceKey}
                            </caption>
                            <thead>
                              <tr className="border-b border-base-300">
                                <th scope="col" className="font-semibold">
                                  {t('opening.ledger')}
                                </th>
                                <th scope="col" className="font-semibold text-right">
                                  {t('opening.amount')}
                                </th>
                                <th scope="col" className="font-semibold">
                                  {t('opening.allocation')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.ledger.map((entry) => (
                                <tr key={entry.id} className="border-b border-base-300">
                                  <td className="break-all">
                                    {entry.id}
                                    <br />
                                    {entry.source} · {entry.createdAt}
                                  </td>
                                  <td className={NUM}>{entry.delta}</td>
                                  <td>
                                    {decision.state === 'PROVEN_OVERLAP' &&
                                      Number(entry.delta) > 0 && (
                                        <input
                                          className={`${FIELD} w-28`}
                                          aria-label={`${t('opening.allocation')} ${entry.id}`}
                                          type="number"
                                          min="0"
                                          step="1"
                                          value={
                                            decision.allocations.find(
                                              (a) => a.ledgerId === entry.id,
                                            )?.amount ?? ''
                                          }
                                          onChange={(e) =>
                                            update(key, {
                                              ...decision,
                                              allocations: [
                                                ...decision.allocations.filter(
                                                  (a) => a.ledgerId !== entry.id,
                                                ),
                                                ...(e.target.value
                                                  ? [{ ledgerId: entry.id, amount: e.target.value }]
                                                  : []),
                                              ],
                                            })
                                          }
                                        />
                                      )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <label className={`${CHECK_LABEL} items-start py-3`}>
                          <input
                            type="checkbox"
                            className={CHECKBOX}
                            checked={decision.attested}
                            onChange={(e) =>
                              update(key, { ...decision, attested: e.target.checked })
                            }
                          />
                          {t(
                            decision.evidenceMode === 'OPERATOR_STATEMENT'
                              ? 'opening.statementAttestation'
                              : 'opening.attestation',
                          )}
                        </label>
                      </div>
                    )}
                  </details>
                );
              })}
            {Math.max(preview.routingSummary?.length ?? 0, preview.routingIssues?.length ?? 0) >
              visible && (
              <button type="button" className={OUTLINE} onClick={() => setVisible((v) => v + 50)}>
                {t('opening.showMore')}
              </button>
            )}
            {!fresh && (
              <p
                role="status"
                className="border border-warning px-3 py-2 text-sm text-base-content"
              >
                {t('opening.repreview')}
              </p>
            )}
            {!workflow.embedded && (
              <button
                type="button"
                className={PRIMARY}
                disabled={!fresh || !preview.ready || uncertain || !context}
                onClick={guardedConfirm}
              >
                {t('opening.confirm')}
              </button>
            )}
          </>
        )}
      </fieldset>
      {pending && (
        <p role="status" className="text-sm text-base-content">
          {t('opening.pending')}
        </p>
      )}
      {error && (
        <div role="alert" className={ALERT}>
          {error}
          <CrmErrorDetails code={technicalError} />
        </div>
      )}
      {result && (
        <CrmResult receipt={result.batchId}>
          {t(result.alreadyConfirmed ? 'opening.already' : 'opening.done')}
        </CrmResult>
      )}
    </section>
  );
}
